/* eslint-disable no-param-reassign */
import createBaseSurveyMeta from './_BASE';

import {
	computeUuid,
	computeFormattedStationId,
	mapGpsDistance,
	computeDistance,
} from '../util/readings.parse.computeProps';

import { defaultCreateSimplifiedReadings } from '../util/general';
import { createComputeYMinYMax } from '../util/yMinYMax';
import {
	createReadingKeyProps,
	createGetReadingKeys,
	createGetReadingKeyProps,
	createGetColumns,
	createGetSurveyColor,
	createTransformToCsvJson
} from './util';

const INVERT_Y_AXIS = false;

const YMINMAX = { yMin: 3000, yMax: 7000};

const isDefined = v => v !== undefined && v !== null;

const KEY_ANOMALY = 'hasAnom'
const KEY_DOC_ANOMALY = 'hasDocAnom'
const KEY_CP_ANOMALY = 'hasCpAnom'
const KEY_COAT_ANOMALY = 'hasCoatAnom'



// eslint-disable-next-line no-unused-vars
export default _survey => {
	// CONSTANTS

	const gapThreshold = 200;

	// READING KEYS

	const readingKeys = [
		{
			label: 'DOC Anomaly',
			value: 'hasDocAnom',
			seriesType: 'Scatter',
			marker: 'circle',
			isPercentage: true,
			color: '#FF6601',
			showInTooltip: true,
			yAxisUnitDisplayText: 'Anomaly Code'
		},
		{
			label: 'CP Anomaly',
			value: 'hasCpAnom',
			seriesType: 'Scatter',
			marker: 'rectRot',
			isPercentage: true,
			color: '#f42431',
			showInTooltip: true,
			yAxisUnitDisplayText: 'Anomaly Code'
		},
		{
			label: 'Coating Anomaly',
			value: 'hasCoatAnom',
			seriesType: 'Scatter',
			marker: 'triangle',
			isPercentage: true,
			color: '#720B98',
			showInTooltip: true,
			yAxisUnitDisplayText: 'Anomaly Code'
		},
	];
	const readingKeyProps = createReadingKeyProps(readingKeys);
	const _computeYMinYMax = createComputeYMinYMax(readingKeys);
	const computeYMinYMax = (survey, currReading) => {
		_computeYMinYMax(survey, currReading);
		survey.yMin = YMINMAX.yMin;
		survey.yMax = YMINMAX.yMax;
	}

	// SKIP READING

	const isSkipReading = reading =>
		reading && readingKeys.every(rk => reading[rk.value] === 0);

	// HAS GAP

	const hasGap = (currReading, prevReading) => {
		return !!prevReading && currReading.computedDistance - prevReading.id > gapThreshold;
	};

	// INTERPOLATE

	const interpolate = (
		// eslint-disable-next-line no-unused-vars
		_prevReading,
		// eslint-disable-next-line no-unused-vars
		_nextReading,
		// eslint-disable-next-line no-unused-vars
		_alignToReading
	) => {};

	// COMPUTE PROPS - to be used by readings.parse

	const computeHilo = r => {
		r.low = r.low || 0;
	};

	const computeAnomalies = r => {
		const { aliaslist: _aliaslist } = r;
		const aliaslist = (_aliaslist || '').toString().replaceAll('\x00', '').trim()
		const aliasarray = (aliaslist || '').toString().split(';').filter(v => v);
		const aliasmap = aliasarray.reduce((acc, code) => {
			acc[code.toString()] = parseInt(code, 10);
			return acc;
		}, {});

		if (aliasmap['4000']) {
			r[KEY_ANOMALY] = 'YES';
			r[KEY_DOC_ANOMALY] = aliasmap['4000'];
		}

		if (aliasmap['5000']) {
			r[KEY_ANOMALY] = 'YES';
			r[KEY_CP_ANOMALY] = aliasmap['5000'];
		}

		if (aliasmap['6000']) {
			r[KEY_ANOMALY] = 'YES';
			r[KEY_COAT_ANOMALY] = aliasmap['6000'];
		}
	}

	const computeProps = (survey, _prevReading, currReading, currIdx) => {
		computeUuid(currReading, currIdx);
		computeFormattedStationId(currReading);
		computeHilo(currReading);
		computeYMinYMax(survey, currReading);
		mapGpsDistance(currReading);
		computeDistance(_prevReading, currReading);
		computeAnomalies(currReading);
	};

	// SIMPLIFIED READINGS - creates a smaller data set to be used for rendering polyline on map
	const createSimplifiedReadings = defaultCreateSimplifiedReadings;

	const filterReadingsOfUnsupportedSurveyType = readings => {
		if (!readings) {
			return readings;
		}

		return readings.filter(r => r.ir_pct !== null && r.ir_pct !== undefined);
	};

	const columns = [
		{ label: 'ID', value: 'id', checked: true, width: 48, required: true },
		{
			label: 'Station',
			value: 'formattedDst',
			checked: true,
			width: 80,
			showInTooltip: false
		},
		{ label: 'ANOM', value: KEY_ANOMALY, checked: true, width: 64 },
		{ label: 'DOC ANOM', value: KEY_DOC_ANOMALY, checked: true, width: 72 },
		{ label: 'CP ANOM', value: KEY_CP_ANOMALY, checked: true, width: 72 },
		{ label: 'COATING ANOM', value: KEY_COAT_ANOMALY, checked: true, width: 72 },
		{
			label: 'LAT',
			value: 'lat',
			checked: true,
			width: 65,
			getCsvValue: r => {
				if (isDefined(r.lat)) {
					return r.lat;
				}
				if (isDefined(r.loc)) {
					return r.loc[0];
				}
				return undefined;
			}
		},
		{
			label: 'LNG',
			value: 'lng',
			checked: true,
			width: 70,
			getCsvValue: r => {
				if (isDefined(r.lng)) {
					return r.lng;
				}
				if (isDefined(r.loc)) {
					return r.loc[1];
				}
				return undefined;
			}
		},
		{
			label: 'COMMENTS',
			value: 'cmt',
			checked: true,
			width: 200,
			type: 'text'
		}
	];

	// MAIN
	const getReadingKeys = createGetReadingKeys(readingKeys);
	const getColumns = createGetColumns(columns);
	const transformToCsvJson = createTransformToCsvJson(getColumns);

	const meta = {
		...createBaseSurveyMeta(_survey),
		isChartInversed: INVERT_Y_AXIS,
		getReadingKeys,
		getReadingKeyProps: createGetReadingKeyProps(readingKeyProps),
		getColumns,
		transformToCsvJson,
		getSurveyColor: createGetSurveyColor(getReadingKeys),
		fkey: 'none',
		isSkipReading,
		hasGap,
		interpolate,
		computeProps,
		computeYMinYMax,
		createSimplifiedReadings,
		filterReadingsOfUnsupportedSurveyType
	};

	return meta;
};
